import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { catalogApi, isProduction } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  SparklesIcon,
  StarIcon,
  UserGroupIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

interface Category {
  categoryId: string;
  name: string;
  slug: string;
  listingCount: number;
}

interface ListingCard {
  listingId: string;
  organizationName: string;
  headline: string;
  description: string;
  logo?: string;
  categories: string[];
  commissionHighlight: string;
  cookieDuration: number;
  tier: 'basic' | 'featured' | 'spotlight';
  badges: string[];
  applicationMode: 'auto_approve' | 'manual_review';
  stats: {
    activeAffiliates: number;
  };
}

interface FeaturedData {
  spotlight: ListingCard[];
  featured: ListingCard[];
}

export default function Catalog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState<ListingCard[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [featured, setFeatured] = useState<FeaturedData>({ spotlight: [], featured: [] });
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const { isAuthenticated, user } = useAuthStore();

  const getDashboardLink = () => {
    if (!isAuthenticated) return '/login';
    if (user?.role === 'superadmin') return '/admin';
    if (user?.role === 'org_admin') return '/org';
    return '/dashboard';
  };

  useEffect(() => {
    loadData();
  }, [searchParams]);

  useEffect(() => {
    loadFeatured();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {
        category: searchParams.get('category') || undefined,
        search: searchParams.get('search') || undefined,
        page: parseInt(searchParams.get('page') || '1', 10),
        limit: 12,
      };

      const response = await catalogApi.browse(params);

      if (response.data.success) {
        setListings(response.data.data.listings || []);
        setCategories(response.data.data.categories || []);
        setPagination(response.data.data.pagination || { page: 1, total: 0, totalPages: 0 });
      }
    } catch (error) {
      console.error('Failed to load catalog:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFeatured = async () => {
    try {
      const response = await catalogApi.getFeatured();
      if (response.data.success) {
        setFeatured({
          spotlight: response.data.data.spotlight || [],
          featured: response.data.data.featured || [],
        });
      }
    } catch (error) {
      console.error('Failed to load featured listings:', error);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (search) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    params.delete('page');
    setSearchParams(params);
  };

  const handleCategoryChange = (slug: string) => {
    const params = new URLSearchParams(searchParams);
    if (slug) {
      params.set('category', slug);
    } else {
      params.delete('category');
    }
    params.delete('page');
    setSelectedCategory(slug);
    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const ListingCardComponent = ({ listing }: { listing: ListingCard }) => (
    <Link
      to={`/catalog/${listing.listingId}`}
      className={`bg-warm-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all border border-tan/20 ${
        listing.tier === 'spotlight' ? 'ring-2 ring-rust/50' :
        listing.tier === 'featured' ? 'ring-1 ring-sage/30' : ''
      }`}
    >
      <div className="space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {listing.logo ? (
              <img src={listing.logo} alt="" className="w-12 h-12 rounded-lg object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-cream flex items-center justify-center">
                <span className="text-xl font-serif font-bold text-charcoal/60">
                  {listing.organizationName.charAt(0)}
                </span>
              </div>
            )}
            <div>
              <h3 className="font-display font-semibold text-charcoal">{listing.organizationName}</h3>
              <div className="flex gap-1 mt-1">
                {listing.badges.map((badge) => (
                  <span
                    key={badge}
                    className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                      badge === 'Spotlight' || badge === 'Top Pick'
                        ? 'bg-rust/10 text-rust'
                        : 'bg-sage/10 text-sage'
                    }`}
                  >
                    {badge}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <p className="text-sm text-charcoal/70 line-clamp-2">{listing.headline}</p>

        <div className="flex flex-wrap gap-1">
          {listing.categories.map((cat) => {
            const category = categories.find(c => c.slug === cat);
            return (
              <span key={cat} className="px-2 py-0.5 bg-cream text-charcoal/70 text-xs rounded">
                {category?.name || cat}
              </span>
            );
          })}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-tan/20">
          <div className="text-sm">
            <span className="text-rust font-serif font-medium">{listing.commissionHighlight}</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-charcoal/50">
            <span className="flex items-center gap-1">
              <ClockIcon className="w-3.5 h-3.5" />
              {listing.cookieDuration}d
            </span>
            <span className="flex items-center gap-1">
              <UserGroupIcon className="w-3.5 h-3.5" />
              {listing.stats.activeAffiliates}
            </span>
          </div>
        </div>

        {listing.applicationMode === 'auto_approve' && (
          <div className="text-xs text-sage flex items-center gap-1">
            <SparklesIcon className="w-3.5 h-3.5" />
            Instant approval
          </div>
        )}
      </div>
    </Link>
  );

  return (
    <div className="min-h-screen bg-cream">
      {/* Navigation */}
      <nav className="bg-charcoal px-6 lg:px-16 py-6 flex justify-between items-center">
        <Link to="/" className="font-display text-2xl font-bold text-warm-white tracking-tight">
          venndela
        </Link>
        <ul className="hidden md:flex items-center gap-12">
          <li>
            <Link to="/" className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
              Home
            </Link>
          </li>
          <li>
            <Link to="/catalog" className="text-rust text-sm font-medium">
              Catalog
            </Link>
          </li>
          <li>
            <Link to="/pricing" className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
              Pricing
            </Link>
          </li>
          {isAuthenticated ? (
            <li>
              <Link to={getDashboardLink()} className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
                Dashboard
              </Link>
            </li>
          ) : (
            <li>
              <Link to="/login" className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
                Log In
              </Link>
            </li>
          )}
        </ul>
      </nav>

      {/* Header */}
      <div className="bg-warm-white border-b border-tan/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-4">
              Affiliate Program Catalog
            </h1>
            <p className="text-lg text-charcoal/70 max-w-2xl mx-auto">
              Discover affiliate programs that match your audience and start earning commissions
            </p>
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="mt-8 max-w-2xl mx-auto">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal/40" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search programs by name, category, or commission..."
                className="w-full pl-12 pr-4 py-3 bg-cream border border-tan/30 rounded-lg text-charcoal placeholder-charcoal/40 focus:outline-none focus:border-rust focus:ring-1 focus:ring-rust"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-rust text-warm-white px-4 py-1.5 rounded-md text-sm font-medium hover:bg-rust/90 transition-colors"
              >
                Search
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Spotlight Carousel */}
        {featured.spotlight.length > 0 && !searchParams.get('search') && !searchParams.get('category') && (
          <div className="mb-12">
            <div className="flex items-center gap-2 mb-6">
              <SparklesIcon className="w-6 h-6 text-rust" />
              <h2 className="font-display text-2xl font-bold text-charcoal">Spotlight Programs</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.spotlight.map((listing) => (
                <ListingCardComponent key={listing.listingId} listing={listing} />
              ))}
            </div>
          </div>
        )}

        {/* Featured Section */}
        {featured.featured.length > 0 && !searchParams.get('search') && !searchParams.get('category') && (
          <div className="mb-12">
            <div className="flex items-center gap-2 mb-6">
              <StarIcon className="w-6 h-6 text-sage" />
              <h2 className="font-display text-2xl font-bold text-charcoal">Featured Programs</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {featured.featured.map((listing) => (
                <ListingCardComponent key={listing.listingId} listing={listing} />
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Categories */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="sticky top-4">
              <div className="bg-warm-white rounded-2xl shadow-sm border border-tan/20 overflow-hidden">
                <div className="px-4 py-3 border-b border-tan/20 flex items-center gap-2">
                  <FunnelIcon className="w-5 h-5 text-charcoal/50" />
                  <h3 className="font-display text-lg font-semibold text-charcoal">Categories</h3>
                </div>
                <div className="p-2">
                  <button
                    onClick={() => handleCategoryChange('')}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      !selectedCategory
                        ? 'bg-rust/10 text-rust font-medium'
                        : 'text-charcoal/70 hover:bg-cream'
                    }`}
                  >
                    All Categories
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => handleCategoryChange(cat.slug)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm flex justify-between items-center transition-colors ${
                        selectedCategory === cat.slug
                          ? 'bg-rust/10 text-rust font-medium'
                          : 'text-charcoal/70 hover:bg-cream'
                      }`}
                    >
                      <span>{cat.name}</span>
                      <span className="text-xs text-charcoal/40">{cat.listingCount}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {/* Results Header */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-xl font-semibold text-charcoal">
                {selectedCategory
                  ? categories.find(c => c.slug === selectedCategory)?.name
                  : 'All Programs'}
                <span className="text-charcoal/50 font-normal ml-2">({pagination.total})</span>
              </h2>
            </div>

            {/* Loading */}
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rust"></div>
              </div>
            ) : listings.length === 0 ? (
              <div className="bg-warm-white rounded-2xl p-12 text-center shadow-sm border border-tan/20">
                <MagnifyingGlassIcon className="w-12 h-12 text-charcoal/30 mx-auto mb-4" />
                <h3 className="font-display text-lg font-medium text-charcoal mb-2">No programs found</h3>
                <p className="text-charcoal/60">
                  Try adjusting your search or filters
                </p>
              </div>
            ) : (
              <>
                {/* Listings Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {listings.map((listing) => (
                    <ListingCardComponent key={listing.listingId} listing={listing} />
                  ))}
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-8">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="px-4 py-2 bg-warm-white border border-tan/30 rounded-lg text-charcoal text-sm font-medium hover:bg-cream disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2 text-charcoal/60">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                      className="px-4 py-2 bg-warm-white border border-tan/30 rounded-lg text-charcoal text-sm font-medium hover:bg-cream disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="bg-charcoal rounded-2xl p-8 md:p-12">
            <h2 className="font-serif text-2xl md:text-3xl text-warm-white mb-4">
              Run an affiliate program?
            </h2>
            <p className="text-tan mb-6 max-w-2xl mx-auto">
              List your program in our catalog and connect with thousands of motivated affiliates
              looking for products to promote.
            </p>
            <Link to={isProduction() ? '/coming-soon' : '/register'} className="inline-block bg-rust text-warm-white px-8 py-3 rounded-lg font-medium hover:bg-rust/90 transition-colors">
              Get Started Free
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-charcoal border-t border-white/10 py-12 px-6 lg:px-16 mt-16">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/" className="font-display text-xl font-bold text-warm-white">
            venndela
          </Link>

          <ul className="flex flex-wrap justify-center gap-8">
            <li>
              <Link to="/" className="text-tan text-sm hover:text-warm-white transition-colors">
                Home
              </Link>
            </li>
            <li>
              <Link to="/catalog" className="text-tan text-sm hover:text-warm-white transition-colors">
                Catalog
              </Link>
            </li>
            <li>
              <Link to="/pricing" className="text-tan text-sm hover:text-warm-white transition-colors">
                Pricing
              </Link>
            </li>
            <li>
              <a href="#" className="text-tan text-sm hover:text-warm-white transition-colors">
                Docs
              </a>
            </li>
            <li>
              <a href="mailto:support@venndela.com" className="text-tan text-sm hover:text-warm-white transition-colors">
                Contact
              </a>
            </li>
          </ul>

          <p className="text-charcoal/50 text-sm">
            © {new Date().getFullYear()} Venndela. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
